import cx from "clsx"
import { Checkbox } from "./Checkbox"
import { describedBy } from "./describedBy"
import { FieldHint } from "./FieldHint"
import styles from "./CheckboxGroup.module.css"

type Option = { value: string; label: string }

type Props = {
  id: string
  label: string
  value: ReadonlyArray<string>
  onChange: (value: string[]) => void
  options: ReadonlyArray<Option>
  /** How to answer it, shown under the label and read with the group. */
  hint?: string
  /** What is wrong with it, shown under the choices and read with the group. */
  error?: string
  required?: boolean
}

// Every choice visible at once, any number choosable: the design's check box
// for each, in a group its legend names. A group cannot be required itself —
// `aria-required` is not allowed on one, and `required` on each box would
// demand that every one be ticked — so it says so in its name, as WAI's
// guidance for grouped controls does.
export function CheckboxGroup({
  id,
  label,
  value,
  onChange,
  options,
  hint,
  error,
  required,
}: Props) {
  return (
    <fieldset
      className={cx(styles.group, { [styles.error]: error })}
      aria-invalid={!!error}
      aria-describedby={describedBy(
        hint && `${id}-hint`,
        error && `${id}-error`,
      )}
    >
      <legend className={styles.label}>
        {label}
        {required && (
          <>
            <span aria-hidden="true" className={styles.required}>
              {" "}
              *
            </span>{" "}
            <span className={styles.srOnly}>(required)</span>
          </>
        )}
      </legend>
      {hint && <FieldHint id={`${id}-hint`}>{hint}</FieldHint>}
      <div className={styles.options}>
        {options.map((option) => (
          <Checkbox
            key={option.value}
            label={option.label}
            checked={value.includes(option.value)}
            onChange={(checked) =>
              onChange(
                checked
                  ? [...value, option.value]
                  : value.filter((chosen) => chosen !== option.value),
              )
            }
          />
        ))}
      </div>
      {error && (
        <span
          id={`${id}-error`}
          className={styles.errorMessage}
          aria-live="polite"
        >
          {error}
        </span>
      )}
    </fieldset>
  )
}
